/**
 * The ⋯ menu for a folder or submission header on the Home scene.
 *
 * The same trigger + backdrop + popover `HomeDocCard` draws for its own
 * Open / Bundle items, lifted out so a header can carry Rename / Delete /
 * Move without a second popover style. Presentational: every item is a
 * label and a callback; the scene decides what the callback does.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import { fonts, inkColors, surfaceColors } from "../tokens";

export interface HomeMenuItem {
  label: string;
  onSelect: () => void;
  /** Rendered in the negative tone — Delete. */
  destructive?: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles({
  wrap: {
    position: "relative",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  trigger: {
    width: "22px",
    height: "22px",
    borderRadius: "5px",
    border: "none",
    background: "transparent",
    color: inkColors.ink4,
    fontSize: "15px",
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": {
      backgroundColor: surfaceColors.paperDim,
      color: inkColors.ink2,
    },
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "default",
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 41,
    minWidth: "150px",
    backgroundColor: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "7px",
    boxShadow: "0 6px 20px rgba(76,51,138,0.16)",
    padding: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  item: {
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink2,
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: "5px",
    padding: "6px 9px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: surfaceColors.paperDim,
    },
    "&:disabled": {
      color: inkColors.ink5,
      cursor: "default",
    },
  },
  destructive: {
    color: "#B42318",
  },
});

export function HomeMenu({ label, items }: { label: string; items: HomeMenuItem[] }): React.ReactElement | null {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        ⋯
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className={styles.backdrop}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div className={styles.menu} role="menu">
            {items.map((item) => (
              <button
                type="button"
                key={item.label}
                role="menuitem"
                disabled={item.disabled}
                className={mergeClasses(styles.item, item.destructive && styles.destructive)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
